import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import CustomDialog from '@/components/ui/Dialog/Dialog';
import FundRulesGrid from '@/components/Regulations/FundRulesGrid';
import { rulesIcon, strokeProps } from '@/components/Regulations/fundRuleGlyphs';
import { FUND_RULES_CONFIRMATION_LABEL, FUND_RULES_DIALOG_TITLE, FUND_RULES_DIALOG_SUBTITLE } from '@/data/fundRulesDeclarations';
import styles from './FundRulesDialog.module.scss';

interface FundRulesDialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  onConfirm: () => void;
  confirmDisabled?: boolean;
}

const FundRulesDialog = ({ open, setOpen, accepted, onAcceptedChange, onConfirm, confirmDisabled = false }: FundRulesDialogProps) => {
  const { t } = useTranslation();

  return (
    <CustomDialog open={open} setOpen={setOpen} className={styles.dialog} hideActionButton fitContent>
      <div className={styles.body}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <span className={styles.titleIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                {rulesIcon}
              </svg>
            </span>
            <h3 className={`${styles.title} body-lr-semibold`}>{FUND_RULES_DIALOG_TITLE}</h3>
          </div>
          <span className={styles.subtitle}>{FUND_RULES_DIALOG_SUBTITLE}</span>
        </div>

        {/* The rule rows themselves are shared with the portal's entry gate (HAT-1256). */}
        <FundRulesGrid />

        <div className={styles.confirmRow}>
          <label className={styles.confirmLabel}>
            <input type="checkbox" className={styles.confirmInput} checked={accepted} onChange={(e) => onAcceptedChange(e.target.checked)} />
            <span className={styles.confirmBox}>
              <svg className={styles.confirmCheck} width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 12.5 10 16.5l8-9" {...strokeProps} strokeWidth={3} />
              </svg>
            </span>
            <span className={styles.confirmText}>{FUND_RULES_CONFIRMATION_LABEL}</span>
          </label>

          <button type="button" className={`${styles.submitButton} body-md-semibold`} onClick={onConfirm} disabled={!accepted || confirmDisabled}>
            {t('openFundRequest.buttons.confirmAndSubmit')}
          </button>
        </div>
      </div>
    </CustomDialog>
  );
};

export default FundRulesDialog;
